/**
 * Fellow Map block — front-end logic.
 *
 * Vanilla, self-contained (no jQuery). Renders an interactive Google Map of
 * `bio` fellows with a zip/city search that lists fellows within 20 miles,
 * sorted by distance. All visual styling lives in SCSS; this file only does
 * data, geometry and layout math.
 *
 * Data is provided by PHP via the global `FellowMapData`:
 *   { fellows: [{name, school, city, link, lat, lng}], iconUrl, styles, initAddress }
 *
 * The Google Maps loader calls `window.initFellowMap` once the API is ready.
 */
( function () {
	'use strict';

	var SEARCH_RADIUS_MILES = 20;
	var METERS_TO_MILES = 0.000621371;
	var DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 }; // Geographic centre of the USA.
	var DEFAULT_ZOOM = 5;

	var map;
	var geocoder;
	var entries = []; // { data, position, marker, el, distanceEl, popup }
	var FellowPopup; // OverlayView subclass, defined once google is available.
	var openEntry = null; // The entry whose popup is currently open.
	var lastTrigger = null; // Element to restore focus to when the popup closes.

	// Cached DOM nodes (resolved in boot()).
	var mapEl;
	var inputEl;
	var submitEl;
	var resultsEl;

	/**
	 * Round a distance to one decimal place.
	 *
	 * @param {number} miles Distance in miles.
	 * @return {number} Distance rounded to one decimal.
	 */
	function roundMiles( miles ) {
		return Math.round( miles * 10 ) / 10;
	}

	/**
	 * Define the custom popup as an OverlayView subclass.
	 *
	 * Gives us a plain <div> we fully own, appended to the map's floatPane and
	 * positioned at the marker's LatLng. There is no Google chrome to hide.
	 */
	function defineFellowPopup() {
		FellowPopup = function ( position, el ) {
			google.maps.OverlayView.call( this );
			this.position = position;
			this.el = el;
			this.el.classList.add( 'module-fellow-map__popup' );

			// Let clicks/scrolls on the popup work without panning/zooming the map.
			if ( typeof google.maps.OverlayView.preventMapHitsAndGesturesFrom === 'function' ) {
				google.maps.OverlayView.preventMapHitsAndGesturesFrom( this.el );
			}
		};

		FellowPopup.prototype = Object.create( google.maps.OverlayView.prototype );

		FellowPopup.prototype.onAdd = function () {
			this.getPanes().floatPane.appendChild( this.el );
		};

		FellowPopup.prototype.onRemove = function () {
			if ( this.el.parentNode ) {
				this.el.parentNode.removeChild( this.el );
			}
		};

		FellowPopup.prototype.draw = function () {
			var projection = this.getProjection();
			if ( ! projection ) {
				return;
			}
			var pixel = projection.fromLatLngToDivPixel( this.position );
			if ( ! pixel ) {
				return;
			}
			// Layout math only — visual offset/arrow live in SCSS.
			this.el.style.left = pixel.x + 'px';
			this.el.style.top = pixel.y + 'px';
		};
	}

	/**
	 * Build a popup element for a fellow with the DOM API.
	 *
	 * Built in JS (not cloned from a server <template>) so the block's markup —
	 * which passes through `the_content` — can't mutate it. The theme strips
	 * empty headings for WCAG 2.4.6, which would otherwise remove the (empty)
	 * popup name heading and break this. Uses the DOM API, not HTML strings.
	 *
	 * @param {Object} entry The fellow entry.
	 * @return {HTMLElement} The popup root element.
	 */
	function buildPopupEl( entry ) {
		var el = document.createElement( 'div' );
		el.className = 'module-fellow-map__popup';
		el.setAttribute( 'role', 'dialog' );
		el.setAttribute( 'aria-label', 'Fellow details' );

		var closeEl = document.createElement( 'button' );
		closeEl.type = 'button';
		closeEl.className = 'module-fellow-map__popup-close';
		closeEl.setAttribute( 'aria-label', 'Close' );
		closeEl.textContent = '×'; // ×
		closeEl.addEventListener( 'click', closeOpenPopup );
		el.appendChild( closeEl );

		// Column 1: all copy (name, school/city, bio link).
		var copyEl = document.createElement( 'div' );
		copyEl.className = 'module-fellow-map__popup-copy';

		var nameEl = document.createElement( 'h3' );
		nameEl.className = 'module-fellow-map__popup-name';
		nameEl.textContent = entry.data.name;
		copyEl.appendChild( nameEl );

		// School / city on separate lines, set as text nodes (no innerHTML).
		var metaEl = document.createElement( 'p' );
		metaEl.className = 'module-fellow-map__popup-meta';
		if ( entry.data.school ) {
			metaEl.appendChild( document.createTextNode( entry.data.school ) );
		}
		if ( entry.data.school && entry.data.city ) {
			metaEl.appendChild( document.createElement( 'br' ) );
		}
		if ( entry.data.city ) {
			metaEl.appendChild( document.createTextNode( entry.data.city ) );
		}
		copyEl.appendChild( metaEl );

		if ( entry.data.link ) {
			var linkEl = document.createElement( 'a' );
			linkEl.className = 'module-fellow-map__popup-link';
			linkEl.href = entry.data.link;
			linkEl.textContent = 'View Bio »'; // »
			copyEl.appendChild( linkEl );
		}

		el.appendChild( copyEl );

		// Column 2: distance from the searched location (hidden until a search runs).
		var distanceEl = document.createElement( 'span' );
		distanceEl.className = 'module-fellow-map__popup-distance';
		distanceEl.hidden = true;
		el.appendChild( distanceEl );
		entry.distanceEl = distanceEl;

		return el;
	}

	/**
	 * Open a fellow's popup, closing any other open popup first.
	 *
	 * @param {Object} entry   The fellow entry to open.
	 * @param {Element} [trigger] Element to restore focus to on close.
	 */
	function openPopup( entry, trigger ) {
		if ( openEntry && openEntry !== entry ) {
			openEntry.popup.setMap( null );
		}

		lastTrigger = trigger || null;

		if ( ! entry.popup ) {
			entry.popup = new FellowPopup( entry.position, entry.el );
		}
		entry.popup.setMap( map );
		openEntry = entry;

		// Smoothly scroll the map to centre on the marker.
		map.panTo( entry.position );

		// Move focus into the dialog for keyboard users.
		var closeEl = entry.el.querySelector( '.module-fellow-map__popup-close' );
		if ( closeEl ) {
			closeEl.focus();
		}
	}

	/**
	 * Close the currently open popup and restore focus.
	 */
	function closeOpenPopup() {
		if ( ! openEntry ) {
			return;
		}
		openEntry.popup.setMap( null );
		openEntry = null;

		if ( lastTrigger && typeof lastTrigger.focus === 'function' ) {
			lastTrigger.focus();
		}
		lastTrigger = null;
	}

	/**
	 * Create a marker + popup element for every fellow with coordinates.
	 */
	function buildEntries() {
		var fellows = ( window.FellowMapData && window.FellowMapData.fellows ) || [];

		fellows.forEach( function ( data ) {
			// Guard each fellow independently — one bad record must never abort
			// the whole loop and leave a partially-populated map.
			try {
				var position = new google.maps.LatLng( data.lat, data.lng );

				var marker = new google.maps.Marker( {
					position: position,
					map: map,
					icon: window.FellowMapData.iconUrl || undefined,
					title: data.name
				} );

				var entry = {
					data: data,
					position: position,
					marker: marker,
					el: null,
					distanceEl: null,
					popup: null,
					distance: null
				};

				entry.el = buildPopupEl( entry );

				marker.addListener( 'click', function () {
					openPopup( entry );
				} );

				entries.push( entry );
			} catch ( err ) {
				if ( window.console && window.console.error ) {
					window.console.error( 'Fellow Map: skipped a fellow that failed to build.', data, err );
				}
			}
		} );
	}

	/**
	 * Update a fellow's popup distance badge after a search.
	 *
	 * @param {Object} entry  The fellow entry.
	 * @param {number} miles  Distance in miles.
	 */
	function setPopupDistance( entry, miles ) {
		if ( ! entry.distanceEl ) {
			return;
		}

		// Number and unit are separate spans so each can be styled
		// independently (e.g. a larger font size on the value). The text node
		// between them preserves the default space when unstyled.
		entry.distanceEl.textContent = '';

		var valueEl = document.createElement( 'span' );
		valueEl.textContent = String( roundMiles( miles ) );

		var unitEl = document.createElement( 'span' );
		unitEl.textContent = 'Miles';

		entry.distanceEl.appendChild( valueEl );
		entry.distanceEl.appendChild( document.createTextNode( ' ' ) );
		entry.distanceEl.appendChild( unitEl );
		entry.distanceEl.hidden = false;
	}

	/**
	 * Render the results sidebar.
	 *
	 * @param {Array} nearby Fellow entries within the search radius, sorted ascending.
	 */
	function renderResults( nearby ) {
		resultsEl.textContent = '';

		var count = document.createElement( 'p' );
		count.className = 'module-fellow-map__results-count';
		count.textContent = nearby.length + ' Results Found';
		resultsEl.appendChild( count );

		if ( ! nearby.length ) {
			return;
		}

		var list = document.createElement( 'ul' );
		list.className = 'module-fellow-map__results-list';

		nearby.forEach( function ( entry, index ) {
			var item = document.createElement( 'li' );
			item.className = 'module-fellow-map__result';

			var button = document.createElement( 'button' );
			button.type = 'button';
			button.className = 'module-fellow-map__result-btn';

			var indexEl = document.createElement( 'span' );
			indexEl.className = 'module-fellow-map__result-index';
			indexEl.textContent = String( index + 1 );

			var body = document.createElement( 'span' );
			body.className = 'module-fellow-map__result-body';

			var nameEl = document.createElement( 'span' );
			nameEl.className = 'module-fellow-map__result-name';
			nameEl.textContent = entry.data.name;

			var distanceEl = document.createElement( 'span' );
			distanceEl.className = 'module-fellow-map__result-distance';
			distanceEl.textContent = roundMiles( entry.distance ) + ' Miles Away';

			body.appendChild( nameEl );
			body.appendChild( distanceEl );
			button.appendChild( indexEl );
			button.appendChild( body );

			button.addEventListener( 'click', function () {
				openPopup( entry, button );
			} );

			item.appendChild( button );

			// Hidden "View Bio" link, preserved from the legacy markup for parity.
			if ( entry.data.link ) {
				var link = document.createElement( 'a' );
				link.className = 'module-fellow-map__result-link';
				link.href = entry.data.link;
				link.textContent = 'View Bio';
				item.appendChild( link );
			}

			list.appendChild( item );
		} );

		resultsEl.appendChild( list );
	}

	/**
	 * Render a polite message when a location can't be geocoded.
	 *
	 * @param {string} query The submitted search term.
	 */
	function renderNoLocation( query ) {
		resultsEl.textContent = '';
		var message = document.createElement( 'p' );
		message.className = 'module-fellow-map__results-count';
		message.textContent = query
			? 'We couldn’t find that location. Try a zip code or city and state.'
			: 'Enter a zip code or city and state to search.';
		resultsEl.appendChild( message );
	}

	/**
	 * Geocode a query, pan the map and list nearby fellows.
	 *
	 * @param {string} query Zip code or city/state.
	 */
	function runSearch( query ) {
		query = ( query || '' ).trim();
		if ( ! query ) {
			return;
		}

		geocoder.geocode( { address: query }, function ( results, status ) {
			if ( 'OK' !== status || ! results || ! results.length ) {
				renderNoLocation( query );
				return;
			}

			var location = results[ 0 ].geometry.location;
			map.panTo( location );

			var nearby = [];

			entries.forEach( function ( entry ) {
				var miles = google.maps.geometry.spherical.computeDistanceBetween( entry.position, location ) * METERS_TO_MILES;
				entry.distance = miles;
				setPopupDistance( entry, miles );

				if ( miles < SEARCH_RADIUS_MILES ) {
					nearby.push( entry );
				}
			} );

			nearby.sort( function ( a, b ) {
				return a.distance - b.distance;
			} );

			renderResults( nearby );
		} );
	}

	/**
	 * Wire up search input, submit button and Esc-to-close.
	 */
	function bindEvents() {
		if ( submitEl ) {
			submitEl.addEventListener( 'click', function () {
				runSearch( inputEl.value );
			} );
		}

		if ( inputEl ) {
			inputEl.addEventListener( 'keydown', function ( event ) {
				if ( 'Enter' === event.key ) {
					event.preventDefault();
					runSearch( inputEl.value );
				}
			} );
		}

		document.addEventListener( 'keydown', function ( event ) {
			if ( 'Escape' === event.key && openEntry ) {
				closeOpenPopup();
			}
		} );
	}

	/**
	 * Initialise the map once the DOM and Maps API are both ready.
	 */
	function boot() {
		mapEl = document.getElementById( 'map' );
		if ( ! mapEl || typeof google === 'undefined' || ! google.maps || ! window.FellowMapData ) {
			return;
		}

		inputEl = document.getElementById( 'fellows-search' );
		submitEl = document.querySelector( '.module-fellow-map__submit' );
		resultsEl = document.querySelector( '.module-fellow-map__results' );

		map = new google.maps.Map( mapEl, {
			center: DEFAULT_CENTER,
			zoom: DEFAULT_ZOOM,
			styles: window.FellowMapData.styles || [],
			zoomControl: true
		} );

		geocoder = new google.maps.Geocoder();

		defineFellowPopup();
		buildEntries();
		bindEvents();

		// Inbound search: if an address arrived via the search form, run it now.
		var initAddress = window.FellowMapData.initAddress;
		if ( initAddress ) {
			if ( inputEl ) {
				inputEl.value = initAddress;
			}
			runSearch( initAddress );
		}
	}

	/**
	 * Google Maps loader callback. May fire before the DOM is ready.
	 */
	window.initFellowMap = function () {
		if ( 'loading' === document.readyState ) {
			document.addEventListener( 'DOMContentLoaded', boot );
		} else {
			boot();
		}
	};
}() );
